/**
 * Anonymous page-view tracker.
 * - Only sends a slug (e.g. "home", "process") to /api/track/{event}.
 * - No identifiers, no payload, no cookies.
 * - Throttled with sessionStorage so a refresh in the same tab counts once.
 * - Also detects the ?src= query param (qr, share-copy, share-native) so that
 *   arrivals via QR scans or the Share card can be counted anonymously,
 *   and strips the param from the URL bar afterwards.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PATH_TO_EVENT = {
  '/': 'landing',
  '/app': 'home',
  '/app/process': 'process',
  '/app/rights': 'rights',
  '/app/consent': 'consent',
  '/app/crossed-the-line': 'crossed_the_line',
  '/app/domestic-abuse': 'domestic_abuse',
  '/app/support': 'support',
  '/app/journal': 'journal',
  '/app/guided-journal': 'guided_journal',
  '/app/faq': 'faq',
  '/app/trauma': 'trauma',
  '/app/grounding': 'grounding',
  '/app/help-now': 'help_now',
};

// Allowed values for the ?src= query param. Anything else is ignored.
const ARRIVAL_SOURCES = new Set(['qr', 'share-copy', 'share-native', 'share-print']);

// Optional ?ref=<slug> query param — used for custom partner QR codes
// generated in the admin stats page. Slug must be lowercase kebab-case,
// 1-40 chars. Recorded as arrival-partner-<slug> for per-partner counts.
const REF_RE = /^[a-z0-9][a-z0-9-]{0,39}$/;

// Google-Analytics-style UTM tags. Angela's earlier QR codes use these
// (e.g. ?utm_source=gmp&utm_medium=qr&utm_campaign=matchday). We honour
// them by firing arrival-partner-<utm_source>, and also arrival-qr when
// utm_medium=qr — so aggregate totals stay consistent.
const UTM_SOURCE_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

function send(event) {
  if (!event) return;
  try {
    const key = `tfi_t_${event}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch {
    /* sessionStorage may be blocked — still send once per load */
  }
  // Anonymous browser context: referrer source + local hour + day-of-week. No identifiers.
  let payload = '{}';
  try {
    let ref = '';
    if (document.referrer) {
      try {
        ref = new URL(document.referrer).hostname.toLowerCase();
      } catch {
        ref = '';
      }
    }
    const now = new Date();
    const hour = now.getHours();
    // JS: 0=Sun..6=Sat. Convert to ISO: 0=Mon..6=Sun.
    const jsDow = now.getDay();
    const dow = (jsDow + 6) % 7;
    payload = JSON.stringify({ ref, hour, dow });
  } catch {
    /* ignore */
  }
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(`${API}/track/${event}`, blob);
      return;
    }
  } catch {
    /* fall through */
  }
  try {
    fetch(`${API}/track/${event}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

export default function useAnonymousTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    const event = PATH_TO_EVENT[pathname] || null;
    send(event);

    // Detect arrivals from a QR scan or the Share card and count them once
    // per browser session. Then quietly strip the ?src= param from the URL
    // so the reader's address bar stays clean and future refreshes don't
    // double-count.
    try {
      const url = new URL(window.location.href);
      const src = url.searchParams.get('src');
      const ref = url.searchParams.get('ref');
      const utmSourceRaw = url.searchParams.get('utm_source');
      const utmMediumRaw = url.searchParams.get('utm_medium');
      const utmSource = utmSourceRaw ? utmSourceRaw.toLowerCase() : '';
      const utmMedium = utmMediumRaw ? utmMediumRaw.toLowerCase() : '';
      let changed = false;

      if (src && ARRIVAL_SOURCES.has(src)) {
        send(`arrival-${src}`);
        url.searchParams.delete('src');
        changed = true;
      }
      if (ref && REF_RE.test(ref)) {
        send(`arrival-partner-${ref}`);
        url.searchParams.delete('ref');
        changed = true;
      }
      // Standard UTM tags — fire a partner event and (if medium=qr) the
      // aggregate arrival-qr counter. Then strip every utm_ param.
      if (utmSource && UTM_SOURCE_RE.test(utmSource)) {
        send(`arrival-partner-${utmSource}`);
        if (utmMedium === 'qr') {
          send('arrival-qr');
        }
      }
      UTM_KEYS.forEach((k) => {
        if (url.searchParams.has(k)) {
          url.searchParams.delete(k);
          changed = true;
        }
      });

      if (changed) {
        const clean = url.pathname + (url.search ? url.search : '') + url.hash;
        window.history.replaceState({}, '', clean);
      }
    } catch {
      /* ignore — URL parsing failures shouldn't break the app */
    }
  }, [pathname]);
}

/** Manually fire a custom event (e.g. for FAB opens, call buttons). */
export function trackEvent(event) {
  send(event);
}
